import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import Timer from '@comp/Timer/Timer';
import Logo from '@comp/Logo/Logo';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import {
  getCalculatorType,
  getCurrentSession,
  getGoalUId,
  getOngoingSectionDetails,
  getQuizDetails
} from '@selectors/quizTaking';
import Button from '@base/Button/Button';
import Image from '@base/Image';
import Info from '@icon/Info';
import { getIsTestRevampGoal } from '@api/selectors/constants';
import { setModalState } from '@actions/quizTaking';
import {
  ATTEMPT_SUMMARY_TYPE,
  mediaQuery,
  MODAL_TYPES
} from '@cont/Quiz/constants';
import { useToggleable } from '@stores/toggleables';
import useConstants from '@api/hooks/useConstants';
import { calculatorType } from '@constants/index';
import useFinishQuiz from '../Taking/Common/useFinishQuiz';
import useFailedAPIQueue from '../Taking/Common/useFailedAPIQueue';
import useQuestion from '../Taking/Objective/Question/useQuestion';
import { getIsGeneralInstructions } from '../Details/utils';

const Wrapper = styled.div`
  position: sticky;
  top: ${Spacings.SPACING_0B};
  z-index: 1;
  width: 100%;
  height: ${Spacings.SPACING_18B};
  background-color: var(--color-base-1);
`;

const HeaderSection = styled.div`
  margin: 0 ${Spacings.SPACING_10B};
  display: grid;
  grid-template-columns: 1fr 272px;
  height: ${Spacings.SPACING_18B};
  column-gap: ${Spacings.SPACING_4B};

  ${mediaQuery.aboveTablet} {
    margin: 0 ${Spacings.SPACING_16B};
    column-gap: ${Spacings.SPACING_16B};
  }

  ${mediaQuery.aboveLargeScreen} {
    max-width: 1520px;
    margin: auto;
    column-gap: 112px;
  }
`;

const TimerSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  max-width: 100%;
`;

const EndButton = styled(Button)`
  min-height: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
`;

const Icon = styled.div`
  cursor: pointer;
  border: 1px solid var(--color-divider);
  height: ${Spacings.SPACING_10B};
  width: ${Spacings.SPACING_10B};
  border-radius: ${Spacings.SPACING_1B};
  background-color: var(--color-base-1);
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: var(--color-i-gray-v2);
  }
  margin-right: ${Spacings.SPACING_14};
`;

const Header = () => {
  const { finishQuiz } = useFinishQuiz();
  const currentSession = useSelectorWithShallowEqual(getCurrentSession);
  const ongoingSectionDetails = useSelectorWithShallowEqual(
    getOngoingSectionDetails
  );
  const router = useRouter();
  const {
    query: { quizUID }
  } = router;

  const hasSectionalTimer = !!ongoingSectionDetails.section;

  const getRemainingTime = useCallback(
    () =>
      hasSectionalTimer
        ? ongoingSectionDetails.time_remaining
        : currentSession.time_remaining,
    [
      currentSession.time_remaining,
      hasSectionalTimer,
      ongoingSectionDetails.time_remaining
    ]
  );

  const [timeRemaining, setTimeRemaining] = useState(getRemainingTime);
  const { openElement: openInstructions } = useToggleable(
    MODAL_TYPES.QUIZ_INSTRUCTION
  );

  const { openElement: openRevampInstructions } = useToggleable(
    MODAL_TYPES.REVAMPED_INSTRUCTIONS_DRAWER
  );

  const { openElement: openSummaryModal } = useToggleable(
    MODAL_TYPES.ATTEMPT_SUMMARY
  );

  useEffect(() => {
    setTimeRemaining(getRemainingTime());
  }, [getRemainingTime]);

  const { forceProcessAPISImmediately } = useFailedAPIQueue();
  const dispatch = useDispatch();

  const goalUID = useSelectorWithShallowEqual(getGoalUId(quizUID));
  const isTestRevampFlow = useConstants(getIsTestRevampGoal(goalUID));
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const isGeneralIstructions = getIsGeneralInstructions(
    quizDetails.instructions
  );

  const { sectionUID } = useQuestion();
  const sectionalCalculatorType = useSelectorWithShallowEqual(
    getCalculatorType(sectionUID)
  );

  const key = hasSectionalTimer ? ongoingSectionDetails.section.uid : 'default';

  const showCalculator =
    calculatorType.CALCULATOR_NOT_SELECTED !== sectionalCalculatorType;

  const openSummaryModalForTest = () => {
    openSummaryModal();
    dispatch(
      setModalState({
        type: ATTEMPT_SUMMARY_TYPE.TEST,
        data: {}
      })
    );
  };

  const handleCalulatorClick = () => {
    dispatch(
      setModalState({
        type: MODAL_TYPES.QUIZ_CALCULATOR,
        data: {
          calculatorType: sectionalCalculatorType,
          isTestRevampFlow
        }
      })
    );
  };

  const handleInstructionsClick = () => {
    if (isTestRevampFlow && isGeneralIstructions) {
      return openRevampInstructions();
    }
    openInstructions();
  };

  return (
    <>
      <Wrapper>
        <HeaderSection>
          <TimerSection>
            <Logo disabled />
            <Timer
              key={key}
              remainingSeconds={timeRemaining}
              onExpire={finishQuiz}
              color="var(--color-text-primary)"
              threshold={10}
              onThresholdReached={forceProcessAPISImmediately}
            />
          </TimerSection>
          <Actions>
            {showCalculator && (
              <Icon onClick={handleCalulatorClick}>
                <Image
                  src="quiz/calculator-new.svg"
                  width="24"
                  height="24"
                  alt="calculator"
                />
              </Icon>
            )}

            <Icon onClick={handleInstructionsClick}>
              <Info size="24px" color="var(--color-text-primary)" />
            </Icon>

            <EndButton
              color="red"
              variant="hollow"
              label="End test"
              width="168px"
              onClick={openSummaryModalForTest}
            />
          </Actions>
        </HeaderSection>
      </Wrapper>
    </>
  );
};

export default Header;
